/**
 * Shared per-cell attention logic — single source of truth for both
 * "Übersicht" and "Vergleichen" views. Without this, both views would
 * implement attention/jump/breakdown independently and drift apart.
 *
 * A cell needs attention if EITHER:
 *   - Confidence-based cellStatus is korrigieren or pruefen, OR
 *   - Plausibility (value/sequence/symbol-exclusivity) is red or yellow
 *
 * User can "accept as-is" via cellAccepted set → the cell drops out of
 * the attention pool (next refresh re-evaluates).
 *
 * The two existing signals (Konfidenz + Plausibilität) are independent
 * dimensions: a low-conf 08:00 (ok value) is still attention-worthy, and
 * a high-conf 08:03 (red value) is attention-worthy too. We aggregate
 * them so the user has ONE list to work through.
 */
import { cellStatus, needsAttention, type CellStatus, type CellThresholds } from "./cellStatus";
import { cellPlausibility, type DayTimes, type TimeField, type Plausibility } from "./plausibility";
import { classify, type Klasse } from "./validator";

export type FieldName = TimeField | "symb";

export interface CellInputs {
  field: FieldName;
  value: string | undefined;
  origValue: string | undefined;
  confidence: number | undefined;
  klasse: Klasse | undefined;
  day: DayTimes;
}

export interface CellAttentionResult {
  /** Whether the cell needs the user's attention (sum of all signals). */
  needs: boolean;
  /** Confidence-based status (legacy cellStatus). */
  status: CellStatus;
  /** Plausibility severity (ok | yellow | red). */
  plaus: Plausibility;
  /** Human-readable plausibility reasons (for tooltip). */
  plausReasons: string[];
}

export function cellAttention(
  inp: CellInputs,
  thresholds: CellThresholds,
  accepted: ReadonlySet<string>,
  acceptKey: string,
): CellAttentionResult {
  // Live klasse: if user has edited the value, re-classify; else trust OCR-time klasse.
  const liveKlasse: Klasse | undefined =
    (inp.value ?? "") !== (inp.origValue ?? "")
      ? classify(inp.value, inp.field).klasse
      : inp.klasse;

  const status = cellStatus(liveKlasse, inp.confidence, inp.value, thresholds);
  const plausResult = cellPlausibility(inp.field, inp.value, inp.day);

  const isAccepted = accepted.has(acceptKey);
  // Confidence layer: pruefen/korrigieren = attention. autoFixed/ok/ignoriert = not.
  const needsConf = needsAttention(status);
  // Plausibility layer: any red/yellow = attention.
  const needsPlaus = plausResult.severity !== "ok";

  return {
    needs: !isAccepted && (needsConf || needsPlaus),
    status,
    plaus: plausResult.severity,
    plausReasons: plausResult.reasons,
  };
}

/** Build the acceptance-key for a (day, field) pair. */
export function attentionKey(day: number, field: FieldName): string {
  return `d${day}_${field}`;
}
